import { FormAutocompleteField } from '@components/FormFields';
import { Label } from '@components/Label';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { Box, FormControlLabel } from '@mui/material';
import { usePrevious } from '@pages/Selections/hooks/usePrevious';
import { selectionsApiSlice } from '@src/api';
import { PortalContext } from '@src/context/portal';
import { useDebounce } from '@src/hooks/useDebounce';
import { getFullName, GUID_EMTY } from '@src/utils';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLoadingContext } from '../ContentLoading';

type OptionValue = {
  name: string;
  value: string;
  key: string;
  accountId: string;
};
export const OrderingProviderSelect = () => {
  const { control, watch, setValue } = useFormContext();
  const { userid } = useContext(PortalContext);
  const { setLoadingState } = useLoadingContext() || {};
  const laboratoryId = watch('laboratoryId');
  const [search, setSearch] = useState('');
  const [currentUser, setCurrentUser] = useState<Nullable<OptionValue>>(null);
  const debounsedSearch = useDebounce(search);
  const [showLoader, setShowLoader] = useState(false);
  const { providers, isFetching } = selectionsApiSlice.useGetOrderingProvidersQuery(
    { laboratoryId, search: debounsedSearch },
    {
      skip: !laboratoryId,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        providers: [
          { name: 'Not applicable', value: GUID_EMTY, key: 'Not applicable', accountId: GUID_EMTY },
          ...(data || []).map(({ id, accountId, ...name }) => ({
            name: getFullName(name),
            value: id,
            key: id,
            accountId: accountId,
          })),
        ],
      }),
    },
  );

  const prevLabId = usePrevious(laboratoryId);

  useEffect(() => {
    if (laboratoryId && prevLabId !== laboratoryId) {
      setShowLoader(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId]);

  useEffect(() => {
    if (!isFetching) {
      setShowLoader(false);
    }
  }, [isFetching]);

  useEffect(() => {
    if (providers && !search) {
      setLoadingState && setLoadingState(prev => ({ ...prev, providers: true }));
      if (userid) {
        const currentUserIsOp = providers.find(({ accountId }) => accountId === userid);

        if (currentUserIsOp) {
          setCurrentUser(currentUserIsOp);
          setValue('providerId', currentUserIsOp.value, { shouldValidate: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers?.length, setValue, userid]);

  const onSearchOP = useCallback((_e, value: string, reason) => {
    if (reason === 'input') setSearch(value || '');
  }, []);

  const onChangeProvider = () => {
    setValue('additionalProductIds', [], { shouldValidate: true });
  };

  return showLoader ? (
    <Box position="relative" mt="18px" height="42px" width="56px">
      <PreloaderBubbles position="absolute" sx={{ left: 0, top: 0, bgcolor: 'transparent' }} />
    </Box>
  ) : (
    <FormControlLabel
      sx={{ width: '100%' }}
      control={
        <FormAutocompleteField
          name="providerId"
          withoutLocalValue
          editValue={currentUser || null}
          control={control}
          onSearch={onSearchOP}
          placeholder="Select"
          clearable={false}
          onChange={onChangeProvider}
          onClear={onChangeProvider}
          options={providers || []}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root.Mui-disabled': {
              background: '#fff',
            },
          }}
          disabled={!laboratoryId}
        />
      }
      label={<Label label="Ordering provider" disabled={!laboratoryId} />}
      labelPlacement="top"
    />
  );
};
